import { useCallback, useState } from 'react';
import { BackButton } from '../../components/BackButton';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { StickyFooter } from '../../components/StickyFooter';
import { TopBar } from '../../components/TopBar';
import { ExtrasCard } from '../../composites/ExtrasCard';
import { ExtrasEditModal } from '../../composites/ExtrasEditModal';
import type { ExtrasEditModalType, ExtrasEditResult } from '../../composites/ExtrasEditModal';
import { GuestSelectModal } from '../../composites/GuestSelectModal';
import type { GuestOption } from '../../composites/GuestSelectModal/GuestSelectModal.types';
import { GridArea, HeadingArea } from './ExtrasTemplate.styles';
import type { ExtrasItem, ExtrasTemplateProps } from './ExtrasTemplate.types';

const OPTION_TYPES = [
  'per_date',
  'per_date_range',
  'per_time_range',
  'per_date_time',
  'per_date_time_range',
] as const;

const isOptionType = (item: ExtrasItem) =>
  !!item.type && (OPTION_TYPES as readonly string[]).includes(item.type);

const usesQuantityModal = (item: ExtrasItem) =>
  item.type === 'per_stay' && item.perPerson === false && (item.maxQuantity ?? 1) > 1;

export function ExtrasTemplate({
  items,
  imageAspectRatio = '1 / 1',
  imageMobileAspectRatio = '3 / 2',
  totalAmount,
  originalAmount,
  currencyCode,
  locale,
  guests,
  onBack,
  onAdd,
  onRemove,
  onConfirmGuestSelection,
  onConfirmSelection,
  onCtaClick,
  ctaDisabled,
  labels,
}: ExtrasTemplateProps) {
  const {
    title,
    subtitle,
    backAriaLabel,
    add,
    remove,
    quantity,
    editSelections,
    noImage,
    footerTitle,
    footerTotal,
    ctaLabel,
    modalTitle,
    modalConfirm,
    modalClose,
    modalTotal,
    dateModalTitle,
    dateRangeModalTitle,
    timeRangeModalTitle,
    dateTimeModalTitle,
    dateTimeRangeModalTitle,
    quantityModalTitle,
    applyToAll,
    perUnit,
    perGuest,
    weekdays,
    previousWeek,
    nextWeek,
    timesLabel,
  } = labels;

  const isMultiGuest = (guests?.length ?? 0) > 1;

  const usesGuestModal = useCallback(
    (item: ExtrasItem) =>
      isMultiGuest && (item.perPerson ?? true) && (!item.type || item.type === 'per_stay'),
    [isMultiGuest]
  );

  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [modalGuests, setModalGuests] = useState<GuestOption[]>([]);
  const [editModalItemId, setEditModalItemId] = useState<string | null>(null);

  const openModal = useCallback(
    (itemId: string, preselectedIds?: string[]) => {
      if (!guests) return;
      const item = items.find((i) => i.id === itemId);
      if (!item) return;

      const guestOptions: GuestOption[] = guests.map((guest) => ({
        id: guest.id,
        label: guest.label,
        selected: preselectedIds ? preselectedIds.includes(guest.id) : true,
        price: { amount: item.price.amount, currencyCode: item.price.currencyCode },
      }));

      setModalGuests(guestOptions);
      setModalItemId(itemId);
    },
    [guests, items]
  );

  const handleToggleModalGuest = useCallback((guestId: string) => {
    setModalGuests((prev) =>
      prev.map((g) => (g.id === guestId ? { ...g, selected: !g.selected } : g))
    );
  }, []);

  const handleConfirmModal = useCallback(() => {
    if (!modalItemId || !onConfirmGuestSelection) return;
    const selectedIds = modalGuests.filter((g) => g.selected).map((g) => g.id);
    onConfirmGuestSelection(modalItemId, selectedIds);
    setModalItemId(null);
    setModalGuests([]);
  }, [modalItemId, modalGuests, onConfirmGuestSelection]);

  const handleCloseModal = useCallback(() => {
    setModalItemId(null);
    setModalGuests([]);
  }, []);

  const handleConfirmEditModal = useCallback(
    (result: ExtrasEditResult) => {
      if (!editModalItemId) return;
      onConfirmSelection?.(editModalItemId, result);
      setEditModalItemId(null);
    },
    [editModalItemId, onConfirmSelection]
  );

  const handleAdd = useCallback(
    (item: ExtrasItem) => {
      if (isOptionType(item) || usesQuantityModal(item)) {
        setEditModalItemId(item.id);
      } else if (usesGuestModal(item)) {
        openModal(item.id);
      } else {
        onAdd(item.id);
      }
    },
    [usesGuestModal, openModal, onAdd]
  );

  const handleEditSelections = useCallback(
    (item: ExtrasItem) => {
      if (isOptionType(item) || usesQuantityModal(item)) {
        setEditModalItemId(item.id);
      } else if (usesGuestModal(item) && item.selectedGuestIds) {
        openModal(item.id, item.selectedGuestIds);
      }
    },
    [usesGuestModal, openModal]
  );

  const canEditSelections = useCallback(
    (item: ExtrasItem) => isOptionType(item) || usesQuantityModal(item) || usesGuestModal(item),
    [usesGuestModal]
  );

  const modalItem = modalItemId ? items.find((i) => i.id === modalItemId) : null;
  const editModalItem = editModalItemId ? items.find((i) => i.id === editModalItemId) : null;

  const editModalType: ExtrasEditModalType | null = editModalItem
    ? editModalItem.type === 'per_stay'
      ? 'quantity'
      : (editModalItem.type as ExtrasEditModalType)
    : null;

  const editModalTitles: Partial<Record<ExtrasEditModalType, string | undefined>> = {
    per_date: dateModalTitle,
    per_date_range: dateRangeModalTitle,
    per_time_range: timeRangeModalTitle,
    per_date_time: dateTimeModalTitle,
    per_date_time_range: dateTimeRangeModalTitle,
    quantity: quantityModalTitle,
  };

  return (
    <>
      {onBack && (
        <TopBar $borderless>
          <BackButton onClick={onBack} ariaLabel={backAriaLabel} />
        </TopBar>
      )}

      <PageSection>
        <HeadingArea>
          <PageHeading title={title} subtitle={subtitle} flush />
        </HeadingArea>

        <ContentArea>
          <GridArea>
            <CardGrid $columns={3} $tabletColumns={2} $flushBottom>
              {items.map((item) => (
                <ExtrasCard
                  key={item.id}
                  image={item.image}
                  imageAspectRatio={imageAspectRatio}
                  imageMobileAspectRatio={imageMobileAspectRatio}
                  title={item.title}
                  price={{ ...item.price, locale }}
                  selected={item.selected}
                  quantity={item.quantity}
                  onAdd={() => handleAdd(item)}
                  onRemove={() => onRemove(item.id)}
                  onEditSelections={
                    canEditSelections(item) && item.selected
                      ? () => handleEditSelections(item)
                      : undefined
                  }
                  labels={{ add, remove, quantity, editSelections, noImage }}
                />
              ))}
            </CardGrid>
          </GridArea>
        </ContentArea>
      </PageSection>
      <StickyFooter
        title={footerTitle}
        totalLabel={footerTotal}
        totalAmount={totalAmount}
        originalAmount={originalAmount}
        currencyCode={currencyCode}
        locale={locale}
        ctaLabel={ctaLabel}
        onCtaClick={onCtaClick}
        ctaDisabled={ctaDisabled}
      />
      {isMultiGuest && (
        <GuestSelectModal
          open={modalItemId !== null}
          onClose={handleCloseModal}
          guests={modalGuests}
          onToggleGuest={handleToggleModalGuest}
          onConfirm={handleConfirmModal}
          extraTitle={modalItem?.title}
          currencyCode={currencyCode}
          locale={locale}
          labels={{
            title: modalTitle,
            confirm: modalConfirm,
            close: modalClose,
            total: modalTotal,
          }}
        />
      )}
      {editModalItem && editModalType && (
        <ExtrasEditModal
          key={editModalItem.id}
          open
          onClose={() => setEditModalItemId(null)}
          type={editModalType}
          extraTitle={editModalItem.title}
          options={editModalItem.options}
          guests={
            isMultiGuest && (editModalItem.perPerson ?? false) && editModalType !== 'quantity'
              ? guests
              : undefined
          }
          perPerson={editModalItem.perPerson ?? false}
          initialSelections={editModalItem.selectedOptions}
          initialApplyToAll={editModalItem.appliedToAllGuests}
          unitPrice={{
            amount: editModalItem.price.amount,
            currencyCode: editModalItem.price.currencyCode,
          }}
          maxQuantity={editModalItem.maxQuantity}
          initialQuantity={editModalItem.quantity ?? 1}
          rangeLabel={editModalItem.rangeLabel}
          currencyCode={currencyCode}
          locale={locale}
          onConfirm={handleConfirmEditModal}
          labels={{
            title: editModalTitles[editModalType],
            close: modalClose,
            total: modalTotal,
            confirm: modalConfirm,
            applyToAll,
            perUnit,
            perGuest,
            weekdays,
            previousWeek,
            nextWeek,
            timesLabel,
          }}
        />
      )}
    </>
  );
}
